// Copyright (c) Cosmo Tech.
// Licensed under the MIT license.
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { AddCircle as AddIcon } from '@mui/icons-material';
import { Grid, Typography, IconButton, Stack } from '@mui/material';
import { TooltipInfo, FadingTooltip } from '../../../../ui';
import { TranslationUtils, ScenarioParametersUtils } from '../../../../utils';
import { CustomItem } from './components/CustomItem';

export const ItemList = ({ parameterData, context, parameterValue, setParameterValue, gridItemProps }) => {
  const { t } = useTranslation();

  const patchedFields = useMemo(() => {
    return parameterData?.options?.fields.map((field) => {
      const parameterId = parameterData.id;
      return { ...field, id: `${parameterId}-${field.id}`, idForTranslationKey: `${parameterId}-${field.id}` };
    });
  }, [parameterData?.options?.fields, parameterData.id]);

  const setRowValue = (rowIndex, newRowValue) => {
    const newValue = [...parameterValue];
    newValue.splice(rowIndex, 1, newRowValue);
    setParameterValue(newValue);
  };

  const addRow = () => {
    const newRow = patchedFields.map((field) => field.defaultValue ?? '');
    setParameterValue([...parameterValue, newRow]);
  };

  const deleteRow = (rowIndex) => {
    setParameterValue(parameterValue.filter((_, index) => index !== rowIndex));
  };

  const rows = useMemo(() => {
    return Array.isArray(parameterValue) && parameterValue.length > 0 ? parameterValue : [];
  }, [parameterValue]);

  const disabled = useMemo(
    () => !context.editMode || ScenarioParametersUtils.isParameterReadOnly(parameterData),
    [context.editMode, parameterData]
  );

  return (
    <Grid container item {...gridItemProps}>
      <Grid item>
        <Stack direction="row" spacing={1}>
          <Typography variant="subtitle2" color="textSecondary">
            {t(TranslationUtils.getParameterTranslationKey(parameterData.id), parameterData.id)}
          </Typography>
          <TooltipInfo
            title={t(TranslationUtils.getParameterTooltipTranslationKey(parameterData.id), parameterData.id)}
            variant="small"
          />
        </Stack>
      </Grid>
      <Grid item container style={{ border: '1px solid grey', borderRadius: '5px' }}>
        {rows?.map((row, rowIndex) => (
          <CustomItem
            disabled={disabled}
            key={rowIndex}
            targetDatasetId={context.targetDatasetId}
            fields={patchedFields}
            parameterId={parameterData.id}
            setRowValue={(newRowValue) => setRowValue(rowIndex, newRowValue)}
            deleteRow={() => deleteRow(rowIndex)}
            rowValue={row}
          />
        ))}
      </Grid>
      <Grid item container justifyContent="flex-end">
        <FadingTooltip title={t('genericcomponent.itemList.button.addRow', 'Add row')} disableInteractive={true}>
          <IconButton
            disabled={disabled}
            size="small"
            variant="contained"
            color="primary"
            onClick={addRow}
            sx={{ mt: 2 }}
          >
            <AddIcon />
          </IconButton>
        </FadingTooltip>
      </Grid>
    </Grid>
  );
};

ItemList.propTypes = {
  parameterData: PropTypes.object.isRequired,
  context: PropTypes.object.isRequired,
  parameterValue: PropTypes.any,
  setParameterValue: PropTypes.func.isRequired,
  gridItemProps: PropTypes.object,
};
